<template>
  <Page has-actions :subtitle="`${total} ${$tc('result', total <= 1 ? 1 : 2)}`" :loading="logs_loading">
    <b-loading v-if="logs_loading && !created" :is-full-page="false" :active="true" />
    <div v-if="created && $can('read', 'settings.logs')" slot="actions">
      <div class="field has-addons">
        <div class="is-flex mr-2">
          <b-button v-if="$device.mobile" size="is-small" rounded @click="showMobile()">
            <b-icon icon="magnify" size="is-small" />
          </b-button>
          <b-input ref="search" v-model="columns[4].input" :placeholder="placeholders.endpoint" icon="magnify" rounded
            :size="$device.mobile ? 'is-small' : ''" :class="{ expand: searchFocused, 'hide-search': $device.mobile }"
            @input="getLogs()" @blur="hideMobile()" />
        </div>
      </div>
    </div>

    <Card v-if="created && $can('read', 'settings.logs')" content-class="c-table">
      <div class="columns is-mobile is-multiline m-0 pt-2 pb-1">
        <div class="column is-2-tablet is-6-mobile">
          <b-input-validation v-model="columns[bodyIndex].input" :label="columns[bodyIndex].label" :placeholder="columns[bodyIndex].operators[0].label" hide-details size="is-small"
            @input="getLogs" />
        </div>
        <div class="column is-2-tablet is-6-mobile">
          <b-input-validation v-model="columns[paramsIndex].input" class="ml-3" :label="columns[paramsIndex].label" :placeholder="columns[paramsIndex].operators[0].label" hide-details size="is-small"
            @input="getLogs" />
        </div>
          <div
          class="column is-8-tablet is-12-mobile is-flex is-justify-content-flex-end"
        >
          <DatesPicker @change="changeDates" />
        </div>
      </div>

      <hr class="my-0" />
      <b-table :data="items" :loading="logs_loading" hoverable mobile-cards striped backend-sorting
        :default-sort="defaultSorting" :sort-multiple="sortingMultiple" :sort-multiple-data="sortingPriority"
        sort-multiple-key="shiftKey" :total="total" @sort="sortPressed"
        @sorting-priority-removed="sortingPriorityRemoved">
        <template v-for="head in columns">
          <b-table-column :key="head.key" :visible="head.visible" :field="head.key" :label="head.label"
            :width="head.width" :centered="head.centered" :sortable="head.sortable"
            :searchable="table.filterable && head.filterable" v-bind="head" header-class="sticky-header">
            <template v-if="head.filterable" slot="searchable">
              <template v-if="head.type == 'date'">
                <b-datepicker v-model="head.input" rounded :placeholder="placeholders.date.day" size="is-small" editable
                  :range="head.filter.key == 'dr'" :min-date="new Date('01/01/2013')" @input="getLogs()" />
                <span class="custom-date-filter" @click="showDrop(head.key)">
                  <b-icon icon="filter" size="is-small" />
                </span>
                <b-dropdown :ref="`filter_drop_${head.key}`" aria-role="list" class="is-flex">
                  <b-dropdown-item v-for="operator in head.operators" :key="operator.label" aria-role="listitem"
                    @click="changeFilter(head.key, operator)">
                    {{ operator.label }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>
              <template v-else-if="head.type == 'select'">
                <b-select-validation v-model="head.input" rounded size="is-small" :expanded="true" hide-details
                  @change="getLogs()">
                  <template>
                    <option v-for="(opt, index) in head.filters"
                      :key="typeof opt === 'object' && opt !== null ? opt.id : index"
                      :value="typeof opt === 'object' && opt !== null ? opt.id : opt">
                      {{ typeof opt === 'object' && opt !== null ? opt.label : opt }}
                    </option>
                  </template>
                </b-select-validation>
              </template>
              <template v-else-if="head.type == 'boolean'">
                <b-select-validation v-model="head.input" rounded expanded hide-details size="is-small"
                  @change="getLogs()">
                  <template>
                    <option :value="true">
                      {{ $t("yes") }}
                    </option>
                    <option :value="false">
                      {{ $t("no") }}
                    </option>
                  </template>
                </b-select-validation>
              </template>
              <template v-else-if="
                head.type == 'number' ||
                head.type == 'text' ||
                head.type == 'custom'
              ">
                <b-input v-model="head.input" :placeholder="head.filter.label" rounded hide-details size="is-small"
                  icon-right="filter" icon-right-clickable @icon-right-click="showDrop(head.key)" @input="getLogs" />
                <b-dropdown :ref="`filter_drop_${head.key}`" aria-role="list" class="is-flex">
                  <b-dropdown-item v-for="operator in head.operators" :key="operator.label" aria-role="listitem"
                    @click="changeFilter(head.key, operator)">
                    {{ operator.label }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>

              <template v-else-if="head.type == 'autocomplete'">
                <b-autocomplete v-model="head.input" :placeholder="$t('choose')" rounded hide-details size="is-small"
                  :data="getAutocompleteFilters(head.filters, head.input)" field="label" clearable
                  @select="getLogs()" />
              </template>
            </template>

            <!-- id -->
            <template v-if="head.key == 'id'" v-slot="props">
              <a v-if="$can('read', 'settings.logs')" href="javascript:;" @click="openModal(props.row)">{{
                props.row.id }}</a>
              <span v-else>{{ props.row.id }}</span>
            </template>

            <!-- authID -->
            <template v-else-if="head.key == 'authID'" v-slot="props">
              {{ (props.row.auth.id && props.row.auth.label) || "-"  }}
            </template>

            <!-- direction -->
            <template v-else-if="head.key == 'direction'" v-slot="props">
              {{ props.row.direction }}
            </template>

            <!-- method -->
            <template v-else-if="head.key === 'method'" v-slot="props">
              <b-tag :type="getMethodColor(props.row.method)">
                {{ props.row.method }}
              </b-tag>
            </template>

            <!-- endpoint -->
            <template v-else-if="head.key === 'endpoint'" v-slot="props">
              <b-tooltip v-if="props.row.endpoint.full" :label="props.row.endpoint.full" content-class="wrap-anything" size="is-large" position="is-bottom" multilined>
                {{ props.row.endpoint.short }}
              </b-tooltip>
              <span v-else>{{ props.row.endpoint.short }}</span>
            </template>

            <!-- latency -->
            <template v-else-if="head.key === 'latency'" v-slot="props">
              {{ props.row.latency + " ms" }}
            </template>

            <!-- status -->
            <template v-else-if="head.key === 'status'" v-slot="props">
              <b-tag :type="getStatusColor(props.row.status)">
                {{ props.row.status }}
              </b-tag>
            </template>

            <!-- created_at -->
            <template v-else-if="head.key === 'createdAt'" v-slot="props">
              <span>
                {{
                  (props.row.created_at && props.row.created_at.formatted) ||
                  "-"
                }}
                <span class="has-text-grey-light">{{
                  props.row.created_at && props.row.created_at.time
                }}</span>
              </span>
            </template>

            <!-- ip -->
            <template v-else-if="head.key == 'ip'" v-slot="props">
              <template>
                {{ props.row.ip }}
              </template>
              <b-tooltip v-if="props.row.user_agent" :label="props.row.user_agent" position="is-bottom" multilined>
                <b-icon icon="information-variant-circle-outline" type="is-info" size="is-small" />
              </b-tooltip>
            </template>

            <template v-else v-slot="props">
              <b-icon v-if="typeof props.row[head.key] === 'boolean'" :icon="props.row[head.key] ? 'check' : 'close'"
                :type="props.row[head.key] ? 'is-success' : 'is-danger'" custom-size="mdi-18px" />
              <template v-else>
                {{ props.row[head.key] || "-" }}
              </template>
            </template>
          </b-table-column>
        </template>
        <EmptyBlock slot="empty" icon="ticket-percent" />
      </b-table>
      <Pagination :per-page="perPage" :per-pages="perPages" :total="total" :current-page="currentPage"
        @update-per-page="(val) => (perPage = val)" @update-current-page="(val) => (currentPage = val)" />
    </Card>
    <b-modal v-if="selectedRequest" v-model="showDetailsModal" :width="720">
      <Card>
        <div slot="header">
          <div class="is-flex is-justify-content-space-between px py-4">
            <div>
              <b-tag :type="getMethodColor(selectedRequest.method)">
                {{ selectedRequest.method }}
              </b-tag>
              <b-tag :type="getStatusColor(selectedRequest.status)" class="ml-2">
                {{ selectedRequest.status }}
              </b-tag>
              <b-tooltip v-if="selectedRequest.endpoint.full" :label="selectedRequest.endpoint.full" content-class="wrap-anything" size="is-large" position="is-bottom" multilined :auto-close="['outside']">
                <b class="ml-2">{{ selectedRequest.endpoint.short }}</b>
              </b-tooltip>
              <b class="ml-2" v-else>{{ selectedRequest.endpoint.short }}</b>
              <span
                class="is-clickable ml-1"
                @click="$root.copytoClipboard(selectedRequest.endpoint.full || selectedRequest.endpoint.short)"
              >
                <b-icon
                  size="is-small"
                  icon="content-copy"
                />
              </span>
            </div>
            <div>
              <b-icon :icon="selectedRequest.auth.id ? 'account-key' : 'account-question'" size="is-small" />
              <b class="ml-1">{{ selectedRequest.auth && selectedRequest.auth.label }}</b>
            </div>
          </div>
          <hr class="my-0">
        </div>
        <div class="is-flex is-justify-content-space-between">
          <div>
            <b-icon icon="arrow-left-right-bold" size="is-small" />
            <span class="ml-1">{{ selectedRequest.direction }}</span>
          </div>
          <div>
            <b-icon icon="ip" size="is-small" />
            <span class="mx-1">{{ selectedRequest.ip }}</span>
            <b-tooltip v-if="selectedRequest.user_agent" :label="selectedRequest.user_agent" position="is-bottom" multilined>
              <b-icon icon="information-variant-circle-outline" type="is-info" size="is-small" />
            </b-tooltip>
          </div>
          <div>
            <b-icon icon="calendar" size="is-small" />
            <span class="ml-1">
              {{ (selectedRequest.created_at && selectedRequest.created_at.formatted) || "-" }}
              <span class="has-text-grey-light">{{
                selectedRequest.created_at && selectedRequest.created_at.time
                }}</span>
            </span>
          </div>
          <div>
            <b-icon icon="clock-time-eight-outline" size="is-small" />
            <span class="ml-1">{{ selectedRequest.latency + " ms" }}</span>
          </div>
        </div>
        <div v-if="selectedRequest.params" class="mt-3 is-relative">
          <span
            class="is-clickable copy-clipboard"
            @click="$root.copytoClipboard(JSON.stringify(selectedRequest.params, null, 2))"
          >
            <b-icon
              size="is-small"
              icon="content-copy"
            />
          </span>
          <b class="is-size-6">Params</b>
          <pre><code>{{ JSON.stringify(selectedRequest.params, null, 2) }}</code></pre>
        </div>
        <div v-if="selectedRequest.body" class="mt-3 is-relative">
          <span
            class="is-clickable copy-clipboard"
             @click="$root.copytoClipboard(JSON.stringify(selectedRequest.body, null, 2))"
          >
            <b-icon
              size="is-small"
              icon="content-copy"
            />
          </span>
          <b class="is-size-6">Body</b>
          <pre><code>{{ JSON.stringify(selectedRequest.body, null, 2) }}</code></pre>
        </div>
        <div v-if="selectedRequest.error" class="has-text-danger mt-3">
          <b class="is-size-6">Error</b>
          <span v-html="selectedRequest.error" class="is-block" />
        </div>
      </Card>
    </b-modal>
  </Page>
</template>

<script>
import moment from "moment";
import debounce from "debounce";
import DatesPicker from "@/components/forms/elements/DatesPicker";

export default {
  components: {
    DatesPicker
  },
  data() {
    return {
      showColumnsTogle: false,
      searchFocused: false,
      columns: [],
      sortingMultiple: false,
      sortingPriority: [],
      singleSorting: {},
      defaultSorting: [],
      currentPage: 0,
      perPage: 0,
      perPages: [],
      logs_loading: true,
      total: 0,
      created: false,
      showDetailsModal: false,
      selectedRequest: null,
      detailsModalLoading: false,
      items: [],
      table: {},
      date_filter: {},
      init: true,
      bodyIndex: null,
      paramsIndex: null,
    };
  },
  watch: {
    currentPage() {
      if (!this.logs_loading) this.getLogs();
    },
    perPage() {
      if (!this.logs_loading) this.getLogs();
    },
  },
  created() {
    this.updateTitle(this.$t("api_logs"));
    if (!this.created) this.getLogs(); 
  },
  methods: {
    showMobile() {
      if (this.$device.mobile) {
        this.searchFocused = true;
        this.$refs.search.focus();
      }
    },
    hideMobile() {
      if (this.$device.mobile) {
        this.searchFocused = false;
      }
    },
    closeDrop() {
      this.$refs["dropdown"].toggle();
    },
    showDrop(field) {
      this.$refs[`filter_drop_${field}`][0].toggle();
    },
    initFilters() {
      for (let i = 0; i < this.columns.length; i++) {
        this.columns[i]["filter"] =
          this.columns[i] &&
          this.columns[i].operators &&
          this.columns[i].operators.length
            ? this.columns[i].operators[0]
            : null;
      }
    },
    changeFilter(field, value) {
      let index = this.columns.findIndex((h) => h.key == field);
      this.columns[index].filterable = false;
      this.columns[index].filter = value;
      this.columns[index].filterable = true;
      if (this.columns[index].input) this.getLogs();
    },
    changeDates(dates) {
      this.date_filter.from = dates.date.from;
      this.date_filter.to = dates.date.to;
      if (!this.init) {
        this.getLogs();
      } else {
        this.init = false;
      }
    },
    sortPressed(field, order, event) {
      if (event["shiftKey"]) {
        let existingPriority = this.sortingPriority.filter(
          (i) => i.field === field
        )[0];
        if (existingPriority) {
          existingPriority.order =
            existingPriority.order === "desc" ? "asc" : "desc";
        } else {
          if (this.singleSorting.field)
            this.sortingPriority.push(this.singleSorting);
          this.sortingPriority.push({ field, order });
        }
        this.singleSorting = {};
      } else {
        this.sortingPriority = []; // [{field, order}]
        this.singleSorting = { field, order };
      }
      this.getLogs();
    },
    sortingPriorityRemoved(value) {
      this.sortingPriority = this.sortingPriority.filter(
        (priority) => priority.field !== value
      );
      this.getLogs();
    },
    getAutocompleteFilters(filters, input) {
      return input
        ? filters.filter((option) => {
            return (
              option.label
                .toString()
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            );
          })
        : filters;
    },
    getMethodColor(method) {
      switch (method) {
        case "GET":
          return "is-info";
        case "POST":
          return "is-success";
        case "PUT":
          return "is-warning";
        case "DELETE":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    getStatusColor(status) {
      if (status >= 200 && status < 300) return 'is-success is-light'
      if (status >= 300 && status < 400) return 'is-info is-light'
      if (status >= 400 && status < 500) return 'is-warning is-light'
      if (status >= 500) return 'is-danger is-light'
      return ''
    },
    getLogs: debounce(function () {
      let api_params = "";
      if (this.currentPage && this.perPage)
        api_params = `?pagination=${this.currentPage}:${this.perPage}`;

      if (this.sortingPriority.length > 1) {
        let sortArr = [];
        for (let i = 0; i < this.sortingPriority.length; i++) {
          const sort = this.sortingPriority[i];
          sortArr.push(`${sort.field}:${sort.order}`);
        }
        api_params += `&sorting=${sortArr.join(",")}`;
      } else if (this.sortingPriority.length == 1) {
        api_params += `&sorting=${this.sortingPriority[0].field}:${this.sortingPriority[0].order}`;
      } else if (this.singleSorting.field) {
        api_params += `&sorting=${this.singleSorting.field}:${this.singleSorting.order}`;
      }

      let searchArr = [];

      if (this.table && this.columns) {
        for (let i = 0; i < this.columns.length; i++) {
          const value = this.columns[i];
          if (
            ["endpoint", "ip", "params", "body"].includes(value.key) &&
            value.input &&
            value.input.length != 0 &&
            value.input.length < 3
          )
            return;
          else if (value.input || typeof value.input == "boolean") {
            let obj = {
              c: value.key,
              v: ["createdAt"].includes(
                value.key
              )
                ? moment(value.input).format("YYYY-MM-DD")
                : value.input,
            };
            if (value.filter) obj.o = value.filter.key;
            searchArr.push(obj);
          }
        }
      }

      if (this.date_filter.from && this.date_filter.to) {
        api_params +=
          "&date_from=" +
          this.date_filter.from +
          "&date_to=" +
          this.date_filter.to;
      }

      if (searchArr.length) {
        api_params += this.$root.formatFilter(searchArr);
      }

      this.logs_loading = true;

      this.$axios
        .get("logs" + api_params)
        .then((res) => {
          this.table = res.data.table;
          this.items = res.data.list ? res.data.list : [];

          this.sortingPriority = this.table.sorting.priority.map((item) => {
            return { field: item.key, order: item.order };
          });
          this.sortingMultiple = this.table.sorting.multiple;
          this.singleSorting = this.sortingPriority[0];
          this.currentPage = this.table.pagination.current;
          this.perPage = this.table.pagination.limit;
          this.perPages = this.table.pagination.limits;
          this.total = this.table.pagination.total;

          if (!this.created) {
            this.columns = this.table.columns;
            this.bodyIndex = this.columns.findIndex((h) => h.key == "body");
            this.paramsIndex = this.columns.findIndex((h) => h.key == "params");
            this.defaultSorting = [
              this.sortingPriority[0].field,
              this.sortingPriority[0].order,
            ];
            this.initFilters();
          }

          this.created = true;
        })
        .catch((e) => {
          const code = parseInt(e.response && e.response.status);
          if (code) {
            if (code === 404) {
              this.items = [];
              this.total = 0;
            } else {
              this.clientError(e);
            }
          }
        })
        .finally(() => (this.logs_loading = false));
    }, 200),
    openModal(item) {
      this.selectedRequest = null;
      this.getLog(item.id);
    },
    getLog(id) {
      this.detailsModalLoading = true;
      this.$axios
        .get("logs/" + id)
        .then((res) => {
          this.selectedRequest = res.data;
          this.showDetailsModal = true;
        })
        .catch((e) => this.clientError(e))
        .finally(() => (this.detailsModalLoading = false));
    },
  },
};
</script>